import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Marco de iPhone, en SVG. Vendorizado de Magic UI, traducido a JSX.
 *
 * Igual que `safari.jsx`: la pantalla es un AGUJERO recortado con una máscara
 * (`screenPunch`), y lo que va adentro se posiciona detrás usando los mismos
 * porcentajes derivados de las constantes. Las dos mitades tienen que moverse
 * juntas.
 *
 *
 * EL CAMBIO CONTRA EL ORIGINAL: `children`
 *
 * El componente de Magic UI sólo acepta `src` (imagen) o `videoSrc`, y los
 * mete en un div con `pointer-events-none`. Para el hero eso no sirve: adentro
 * va `SimuladorWhatsApp`, que es un componente vivo escribiendo la conversación
 * mensaje por mensaje. Un GIF de un chat no prueba que el bot funcione; el chat
 * corriendo sí.
 *
 * La rama de `children` usa exactamente la misma geometría que las otras dos
 * —de ahí que esté factorizada en `pantalla`— y NO lleva `pointer-events-none`,
 * porque algo vivo tiene que poder recibir el mouse.
 *
 * `overflow-hidden` + el radio de la pantalla son los que hacen que el
 * contenido quede recortado con las esquinas del teléfono en vez de salirse por
 * arriba del marco.
 */
const PHONE_WIDTH = 433;

/* EL CUERPO DEL APARATO. Todo lo demás se deriva de estas medidas. */
const CUERPO_X = 2;
const CUERPO_Y = 0;
const CUERPO_ANCHO = 428;

/* El alto del aparato, y por lo tanto su PROPORCIÓN. 882 sobre 433 de ancho es
   la de un iPhone: 1 a 2, bien alargada. Es un prop y no una constante porque en
   el hero se quiere una forma más cuadrada —el teléfono va solo y a 1:2 ocupa
   toda la ventana— y porque bajar el alto sin recalcular la pantalla y los
   radios rompe la concentricidad del marco, que ya pasó una vez. Acá el número
   entra por un lado y sale por todos. */
const ALTO_POR_DEFECTO = 882;

/* EL BISEL: cuánto marco blanco hay entre el borde del aparato y la pantalla.
   El original venía en 19.25 escrito de forma implícita —repartido entre cuatro
   números de posición y tamaño— así que afinarlo obligaba a recalcular los
   cuatro a mano y a acertarle. Acá es UN número y la pantalla sale de él. */
const BISEL = 11;

const SCREEN_X = CUERPO_X + BISEL;
const SCREEN_Y = CUERPO_Y + BISEL;
const SCREEN_WIDTH = CUERPO_ANCHO - BISEL * 2;
/* TODOS LOS REDONDEOS SALEN DE ACÁ, y eso es un arreglo.

   El componente original traía los tres contornos —cuerpo, marco blanco y
   pantalla— escritos a mano como paths, cada uno con su radio metido adentro
   del atributo `d`: 73, 70 y 55.75. Esos números NO son arbitrarios, son
   concéntricos: el bisel mide 19.25, y 73 − 19.25 ≈ 55.75. Esa relación es lo
   que hace que el marco blanco tenga espesor parejo en todo el borde.

   Al bajar sólo el radio de la pantalla para que se viera menos redondo, la
   relación se rompió: el blanco se ensanchaba en las esquinas y quedaba fino en
   los lados rectos. Se veía como un marco mal recortado.

   Ahora los contornos se generan con `contorno()` a partir de dos números y
   siempre quedan concéntricos: `RADIO_DEL_MARCO` decide cuán redondo es el
   aparato y `BISEL` cuán grueso es el marco blanco. El radio de la pantalla no
   se elige, se deriva. */
const RADIO_DEL_MARCO = 52;

/** Path de rectángulo redondeado. Evita escribir curvas a mano. */
const contorno = (x, y, w, h, r) => {
  const radio = Math.max(0, Math.min(r, w / 2, h / 2));
  return `M${x + radio} ${y}H${x + w - radio}A${radio} ${radio} 0 0 1 ${x + w} ${y + radio}`
    + `V${y + h - radio}A${radio} ${radio} 0 0 1 ${x + w - radio} ${y + h}`
    + `H${x + radio}A${radio} ${radio} 0 0 1 ${x} ${y + h - radio}`
    + `V${y + radio}A${radio} ${radio} 0 0 1 ${x + radio} ${y}Z`;
};

/* El radio de la pantalla NO se elige: es el del cuerpo menos el bisel. Eso es
   lo que mantiene el blanco de espesor parejo en todo el contorno. */
const SCREEN_RADIUS = RADIO_DEL_MARCO - BISEL;

const LEFT_PCT = (SCREEN_X / PHONE_WIDTH) * 100;
const WIDTH_PCT = (SCREEN_WIDTH / PHONE_WIDTH) * 100;
const RADIUS_H = (SCREEN_RADIUS / SCREEN_WIDTH) * 100;

export function Iphone({ src, videoSrc, children, alto = ALTO_POR_DEFECTO, className, style, ...props }) {
  /* Derivadas del alto. Van adentro porque `alto` es un prop: la pantalla, sus
     porcentajes y el radio vertical tienen que moverse todos juntos. */
  const PHONE_HEIGHT = alto;
  const CUERPO_ALTO = alto;
  const SCREEN_HEIGHT = CUERPO_ALTO - BISEL * 2;
  const TOP_PCT = (SCREEN_Y / PHONE_HEIGHT) * 100;
  const HEIGHT_PCT = (SCREEN_HEIGHT / PHONE_HEIGHT) * 100;
  const RADIUS_V = (SCREEN_RADIUS / SCREEN_HEIGHT) * 100;

  const hasVideo = !!videoSrc;
  const hasMedia = hasVideo || !!src || !!children;

  /* EL SOLAPE DE 0.35% ARREGLA LA LÍNEA CLARA DE LOS BORDES.

     El contenido se posiciona en porcentajes de la caja y el SVG recorta el
     agujero con `rx` en unidades de su `viewBox`. Son dos sistemas distintos
     redondeando por su cuenta, así que en los cantos quedaba una franja de un
     píxel donde asomaba el relleno gris de la pantalla (`fill-[#E5E5E5]`) por
     detrás del contenido: se veía como un hilo de fondo alrededor del chat.

     Estirando el contenido una fracción por fuera del recorte, esa franja queda
     tapada. Lo que sobra lo corta la máscara del SVG, así que no se ve. */
  const SOLAPE = 0.35;

  const pantalla = {
    left: `${LEFT_PCT - SOLAPE}%`,
    top: `${TOP_PCT - SOLAPE / 2}%`,
    width: `${WIDTH_PCT + SOLAPE * 2}%`,
    height: `${HEIGHT_PCT + SOLAPE}%`,
    borderRadius: `${RADIUS_H}% / ${RADIUS_V}%`,
  };

  return (
    <div
      className={cn('relative inline-block w-full align-middle leading-none', className)}
      style={{ aspectRatio: `${PHONE_WIDTH}/${PHONE_HEIGHT}`, ...style }}
      {...props}
    >
      {hasVideo && (
        <div className="pointer-events-none absolute z-0 overflow-hidden" style={pantalla}>
          <video
            className="block size-full object-cover"
            src={videoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          />
        </div>
      )}

      {!hasVideo && src && (
        <div className="pointer-events-none absolute z-0 overflow-hidden" style={pantalla}>
          <img src={src} alt="" className="block size-full object-cover object-top" />
        </div>
      )}

      {!hasVideo && !src && children && (
        <div className="absolute z-0 overflow-hidden" style={pantalla}>
          {children}
        </div>
      )}

      <svg
        viewBox={`0 0 ${PHONE_WIDTH} ${PHONE_HEIGHT}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="pointer-events-none absolute inset-0 size-full"
        style={{ transform: 'translateZ(0)' }}
        aria-hidden="true"
      >
        <g mask={hasMedia ? 'url(#screenPunch)' : undefined}>
          {/* Cuerpo del aparato */}
          <path d={contorno(CUERPO_X, CUERPO_Y, CUERPO_ANCHO, CUERPO_ALTO, RADIO_DEL_MARCO)} className="fill-[#E5E5E5]" />
          <path d="M0 171C0 170.448 0.447715 170 1 170H3V204H1C0.447715 204 0 203.552 0 203V171Z" className="fill-[#E5E5E5]" />
          <path d="M1 234C1 233.448 1.44772 233 2 233H3.5V300H2C1.44772 300 1 299.552 1 299V234Z" className="fill-[#E5E5E5]" />
          <path d="M1 319C1 318.448 1.44772 318 2 318H3.5V385H2C1.44772 385 1 384.552 1 384V319Z" className="fill-[#E5E5E5]" />
          <path d="M430 279H432C432.552 279 433 279.448 433 280V384C433 384.552 432.552 385 432 385H430V279Z" className="fill-[#E5E5E5]" />
          {/* Marco blanco. Radio = el del cuerpo menos su propio grosor (4),
              para que quede concéntrico con él. */}
          <path d={contorno(CUERPO_X + 2, CUERPO_Y + 2, CUERPO_ANCHO - 4, CUERPO_ALTO - 4, RADIO_DEL_MARCO - 2)} className="fill-white" />
        </g>

        {/* Acá iba la ranura del auricular, un resto del notch que se sacó más
            arriba. Sin la isla dinámica quedaba una muesca gris suelta en el
            borde de arriba, sin nada que la explicara. */}

        {/* La pantalla. Su radio es el del cuerpo menos el bisel: eso es lo que
            mantiene el blanco de espesor parejo en todo el contorno. */}
        <path
          d={contorno(SCREEN_X, SCREEN_Y, SCREEN_WIDTH, SCREEN_HEIGHT, SCREEN_RADIUS)}
          className="fill-[#E5E5E5] stroke-[#E5E5E5] stroke-[0.5]"
          mask={hasMedia ? 'url(#screenPunch)' : undefined}
        />

        {/* ACÁ IBA LA ISLA DINÁMICA (tres paths: la píldora, el círculo de
            la cámara y su lente). Se sacó a pedido: en esta maqueta el
            contenido de la pantalla es una conversación de WhatsApp, y la
            píldora negra le caía justo encima del encabezado verde, tapando el
            nombre del negocio. Un notch en una maqueta de landing no aporta
            nada —nadie duda de que es un teléfono— y sí quita lugar útil.
            Están en git si hacen falta. */}

        <defs>
          <mask id="screenPunch" maskUnits="userSpaceOnUse">
            <rect x="0" y="0" width={PHONE_WIDTH} height={PHONE_HEIGHT} fill="white" />
            <rect
              x={SCREEN_X}
              y={SCREEN_Y}
              width={SCREEN_WIDTH}
              height={SCREEN_HEIGHT}
              rx={SCREEN_RADIUS}
              ry={SCREEN_RADIUS}
              fill="black"
            />
          </mask>
        </defs>
      </svg>
    </div>
  );
}

export default Iphone;
